import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
  GOOGLE_MAPS_VERSION,
} from '@/lib/google-maps/libraries';

export interface ParsedAddress {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Props {
  id?: string;
  /** null leaves the label to the caller, who names the box with `ariaLabel`. */
  label?: ReactNode | null;
  ariaLabel?: string;
  maxLength?: number;
  inputClassName?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (parsed: ParsedAddress) => void;
  /** ISO country codes Places may suggest from (e.g. ['us', 'ca']); all when omitted. */
  countries?: string[];
}

function getComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
  nameType: 'long_name' | 'short_name' = 'long_name',
): string {
  return components.find((c) => c.types.includes(type))?.[nameType] ?? '';
}

export function GoogleAddressAutocomplete({
  id = 'address-autocomplete',
  label = 'Street',
  ariaLabel,
  maxLength,
  inputClassName,
  placeholder = '123 Main St',
  value,
  onChange,
  onAddressSelect,
  countries,
}: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  // A string, so an inline array from a caller does not rebuild the widget every render.
  const countryKey = countries?.join(',') ?? '';
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onAddressSelectRef = useRef(onAddressSelect);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    if (typeof google === 'undefined' || !google.maps?.places) return;

    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      fields: ['address_components'],
      ...(countryKey ? { componentRestrictions: { country: countryKey.split(',') } } : {}),
    });

    autocompleteRef.current = ac;

    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const comps = place.address_components ?? [];

      const streetNumber = getComponent(comps, 'street_number');
      const route = getComponent(comps, 'route');
      const street = [streetNumber, route].filter(Boolean).join(' ');

      onAddressSelectRef.current({
        street,
        street2: '',
        city:
          getComponent(comps, 'locality') ||
          getComponent(comps, 'sublocality') ||
          getComponent(comps, 'postal_town'),
        state: getComponent(comps, 'administrative_area_level_1', 'short_name'),
        zip: getComponent(comps, 'postal_code'),
        country: getComponent(comps, 'country', 'short_name'),
      });
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [isLoaded, countryKey]);

  return (
    <div className="space-y-1.5">
      {label === null ? null : <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        {apiKey ? (
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        ) : null}
        <Input
          ref={inputRef}
          id={id}
          aria-label={ariaLabel}
          maxLength={maxLength}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={cn(apiKey && 'pl-9', inputClassName)}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
